/**
 * FIFO queue whose empty consumers can be woken by later producers.
 *
 * The queue is reusable across playback runs. `reset()` cancels current waits
 * without permanently closing the queue, which matches campaign/session stops.
 */
export class AsyncAudioQueue {
  #items = [];

  #waiters = new Set();

  get length() {
    return this.#items.length;
  }

  [Symbol.iterator]() {
    return this.#items[Symbol.iterator]();
  }

  enqueue(items) {
    if (!Array.isArray(items) || items.length === 0) return this.length;
    this.#items.push(...items);
    this.#resolveReadyWaiters();
    return this.length;
  }

  replace(items) {
    this.#items = Array.isArray(items) ? [...items] : [];
    if (this.#items.length > 0) this.#resolveReadyWaiters();
    return this.length;
  }

  drain() {
    const items = this.#items;
    this.#items = [];
    return items;
  }

  clear() {
    this.#items = [];
  }

  reset() {
    this.clear();
    for (const waiter of this.#waiters) waiter.resolve(false);
    this.#waiters.clear();
  }

  waitForItems(isReady = (items) => items.length > 0) {
    if (isReady(this.#items)) {
      return { promise: Promise.resolve(true), cancel() {} };
    }

    let waiter;
    const promise = new Promise((resolve) => {
      waiter = { resolve, isReady };
      this.#waiters.add(waiter);
    });

    return {
      promise,
      cancel: () => this.#waiters.delete(waiter),
    };
  }

  #resolveReadyWaiters() {
    for (const waiter of this.#waiters) {
      if (!waiter.isReady(this.#items)) continue;
      this.#waiters.delete(waiter);
      waiter.resolve(true);
    }
  }
}

export default AsyncAudioQueue;
